import React from "react";

/**
 * EditorialGallery
 * ----------------------------------------------------------
 * "Galerie · für dich" – Statement-Poster im Desenio-Look.
 * Cream-Passepartout-Rahmen mit dezenten Serif-Zitaten.
 * Rotiert wöchentlich (nach ISO-Woche) durch 6 verschiedene Sätze,
 * damit sich die Wand jede Woche „neu einrichtet".
 */

const POSTERS = [
  // Woche A
  [
    {
      kind: "type",
      bg: "#EDE3D3",
      ink: "#4A423A",
      accent: "#8A9A86",
      eyebrow: "N° 01 · Dickies-Campus",
      lines: ["Zähl nicht die Tage.", "Mach, dass die", "Tage zählen."],
      caption: "Haltung",
    },
    {
      kind: "botanica",
      bg: "#F5EDE7",
      ink: "#4A423A",
      accent: "#8A9A86",
      eyebrow: "Botanica · Studio DH",
      main: "Pflege wächst\nin kleinen Schritten.",
      caption: "Ars longa · MMXXVI",
    },
    {
      kind: "sun",
      bg: "#EDE1D7",
      ink: "#6B5A4A",
      accent: "#D9775A",
      eyebrow: "Harmony · MMXXVI",
      main: "Du bist die Ruhe\nim Sturm.",
      caption: "Für das Team",
    },
  ],
  // Woche B
  [
    {
      kind: "botanica",
      bg: "#EEF2ED",
      ink: "#3E4A3B",
      accent: "#D9775A",
      eyebrow: "Meridian · Editorial",
      main: "Guter Unterricht\nist ein Handwerk.",
      caption: "Akademie",
    },
    {
      kind: "type",
      bg: "#F0E5D5",
      ink: "#4A423A",
      accent: "#8A9A86",
      eyebrow: "N° 02 · Dickies-Campus",
      lines: ["Sei nicht", "das lauteste", "Ding im Raum."],
      caption: "Führung",
    },
    {
      kind: "sun",
      bg: "#F5EDE7",
      ink: "#6B5A4A",
      accent: "#D9775A",
      eyebrow: "Aurora · MMXXVI",
      main: "Kleine Schritte,\njeden Tag.",
      caption: "Für Kursleitungen",
    },
  ],
];

// Kleines geometrisches Botanica-Symbol als SVG
const BotanicaLeaf = ({ color = "#8A9A86" }) => (
  <svg viewBox="0 0 120 160" width="88" height="118" aria-hidden="true">
    <path d="M60 145 L60 30" stroke={color} strokeWidth="1.4" fill="none" />
    <path d="M60 55 Q35 45 25 30 Q45 40 60 55 Z" fill={color} opacity="0.85" />
    <path d="M60 55 Q85 45 95 30 Q75 40 60 55 Z" fill={color} opacity="0.85" />
    <path d="M60 80 Q35 70 25 55 Q45 65 60 80 Z" fill={color} opacity="0.85" />
    <path d="M60 80 Q85 70 95 55 Q75 65 60 80 Z" fill={color} opacity="0.85" />
    <path d="M60 105 Q35 95 25 80 Q45 90 60 105 Z" fill={color} opacity="0.85" />
    <path d="M60 105 Q85 95 95 80 Q75 90 60 105 Z" fill={color} opacity="0.85" />
    <circle cx="60" cy="30" r="3" fill={color} />
  </svg>
);

// Sonnen-/Blüten-Symbol
const SunBloom = ({ color = "#D9775A" }) => (
  <svg viewBox="0 0 160 160" width="118" height="118" aria-hidden="true">
    <g stroke={color} strokeWidth="1.4" fill="none" strokeLinecap="round">
      {Array.from({ length: 16 }).map((_, i) => {
        const a = (i * Math.PI * 2) / 16;
        const x1 = 80 + Math.cos(a) * 30;
        const y1 = 80 + Math.sin(a) * 30;
        const x2 = 80 + Math.cos(a) * 62;
        const y2 = 80 + Math.sin(a) * 62;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
      })}
    </g>
    <circle cx="80" cy="80" r="22" fill={color} opacity="0.15" />
    <circle cx="80" cy="80" r="14" fill="none" stroke={color} strokeWidth="1.4" />
  </svg>
);

const isoWeek = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

const PosterCard = ({ poster, testId }) => {
  return (
    <figure
      className="poster-card"
      style={{ background: "#FFFFFF" }}
      data-testid={testId}
    >
      <div className="poster-inner" style={{ background: poster.bg, color: poster.ink }}>
        <div className="poster-eyebrow" style={{ color: poster.accent }}>
          {poster.eyebrow}
        </div>

        {poster.kind === "type" && (
          <div className="poster-lines">
            {poster.lines.map((l, i) => (
              <div key={i} className={`poster-line line-${i}`}>
                {l}
              </div>
            ))}
          </div>
        )}

        {poster.kind === "botanica" && (
          <div className="poster-motif">
            <BotanicaLeaf color={poster.accent} />
            <div className="poster-quote" style={{ color: poster.ink }}>
              {poster.main.split("\n").map((l, i) => (
                <div key={i}>{l}</div>
              ))}
            </div>
          </div>
        )}

        {poster.kind === "sun" && (
          <div className="poster-motif">
            <SunBloom color={poster.accent} />
            <div className="poster-quote" style={{ color: poster.ink }}>
              {poster.main.split("\n").map((l, i) => (
                <div key={i}>{l}</div>
              ))}
            </div>
          </div>
        )}

        <figcaption className="poster-caption" style={{ color: poster.accent }}>
          {poster.caption}
        </figcaption>
      </div>
    </figure>
  );
};

const EditorialGallery = ({ userName = "dich" }) => {
  const week = isoWeek();
  const posters = POSTERS[week % POSTERS.length];

  return (
    <section className="editorial-gallery" data-testid="dashboard-editorial-gallery">
      <div className="editorial-gallery-header">
        <span className="dh-meta-label">Galerie · für {userName}</span>
        <h2 className="editorial-gallery-title">
          Worte, die <span className="dh-serif-italic" style={{ color: "#D9775A" }}>tragen</span>.
        </h2>
      </div>
      <div className="editorial-gallery-grid">
        {posters.map((p, i) => (
          <PosterCard key={i} poster={p} testId={`poster-card-${i}`} />
        ))}
      </div>
    </section>
  );
};

export default EditorialGallery;
